import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ChevronDown } from "lucide-react";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
);

export default function Login() {
  const { user, loginPassword, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showBackup, setShowBackup] = useState(false);

  if (!loading && user) return <Navigate to={user.onboarded ? "/" : "/onboarding"} replace />;

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await loginPassword(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate(u.onboarded ? "/" : "/onboarding", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A1F44] text-white flex items-stretch">
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Team Smith Hub" className="w-11 h-11 rounded-md shadow-md ring-1 ring-[#C9A961]/40" />
          <div>
            <div className="font-serif text-lg tracking-wide leading-tight">Team Smith Hub</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">Connect · Build · Grow · Lead</div>
          </div>
        </div>
        <div className="space-y-5 max-w-lg">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">Welcome to Team Smith Hub</div>
          <h1 className="font-serif text-5xl leading-[1.05]">Your central place to build, track and grow.</h1>
          <p className="text-white/70 text-base leading-relaxed">
            Track your conversations and activity, manage your PBOs, stay on top of upcoming conferences,
            and access the training and resources you need to build and grow your business.
          </p>
          <div className="flex items-center gap-3 pt-2 text-[11px] uppercase tracking-[0.28em] text-[#C9A961]">
            <span>Connect</span><span className="text-[#C9A961]/40">·</span>
            <span>Build</span><span className="text-[#C9A961]/40">·</span>
            <span>Grow</span><span className="text-[#C9A961]/40">·</span>
            <span>Lead</span>
          </div>
        </div>
        <div className="text-xs text-white/40">© Team Smith · Melbourne, AU</div>
        <div className="absolute -right-40 -bottom-40 w-[520px] h-[520px] rounded-full border border-[#C9A961]/20" />
        <div className="absolute -right-24 -bottom-24 w-[360px] h-[360px] rounded-full border border-[#C9A961]/10" />
      </div>

      <div className="flex-1 flex items-center justify-center bg-[#F7F5EF] text-slate-900 px-6 py-14">
        <div className="w-full max-w-sm space-y-6" data-testid="login-page">
          <div className="lg:hidden flex items-center gap-3 mb-5">
            <img src="/logo.png" alt="Team Smith Hub" className="w-12 h-12 rounded-md shadow-sm" />
            <div>
              <div className="font-serif text-lg text-[#0A1F44] leading-tight">Team Smith Hub</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961] font-semibold">Connect · Build · Grow · Lead</div>
            </div>
          </div>
          <p className="lg:hidden text-sm text-slate-600 -mt-2 mb-2">Your central place to build, track and grow.</p>

          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Sign in</div>
            <h2 className="font-serif text-3xl text-[#0A1F44] mt-1">Welcome back.</h2>
            <p className="text-sm text-slate-500 mt-1">Use your approved Team Smith Google account.</p>
          </div>

          <Button
            data-testid="google-signin"
            onClick={google}
            className="w-full h-11 bg-white hover:bg-slate-50 text-slate-900 rounded-full border border-slate-200 shadow-sm"
          >
            <GoogleIcon />
            <span className="ml-2 font-medium">Continue with Google</span>
          </Button>

          <div className="flex items-center gap-3 text-[11px] text-slate-400 uppercase tracking-widest">
            <div className="h-px bg-slate-200 flex-1" />
            or
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <button
            data-testid="toggle-backup"
            onClick={() => setShowBackup((v) => !v)}
            className="w-full flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-800"
          >
            Admin email login
            <ChevronDown className={`w-4 h-4 transition-transform ${showBackup ? "rotate-180" : ""}`} />
          </button>

          {showBackup && (
            <form onSubmit={submitPassword} className="space-y-3" data-testid="password-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input id="email" data-testid="login-email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input id="password" data-testid="login-password" type="password" required
                  value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={busy}
                className="w-full h-11 bg-[#0A1F44] hover:bg-[#0A1F44]/90 text-white rounded-full">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
          )}

          <div className="text-xs text-slate-500 text-center">
            New here? Ask your Team Smith Admin to invite/approve your Google account first.
          </div>
          <div className="text-xs text-center">
            <a href="/privacy" data-testid="login-privacy-link" className="text-[#0A1F44] underline underline-offset-2">Privacy Notice</a>
          </div>
        </div>
      </div>
    </div>
  );
}
